import { asArray, asRecord, firstText } from "@/lib/tina/value-readers";
import type { HitlContext, PipelineItem } from "@/types/tina";

export { asArray, asRecord, firstText };

export interface TextRef {
  id?: string;
  name?: string;
}

export interface MusicEventContext {
  name?: string;
  about?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  genreNames: string[];
  venueName?: string;
  cityName?: string;
  lineupNames: string[];
  timezone?: string;
  ticketingUrl?: string;
  sourceUrl?: string;
  site?: string;
  selectedImage?: SelectedEventImage;
}

export interface SelectedEventImage {
  url?: string;
  ref?: string;
  source?: string;
  classification?: string;
  index?: number;
}

export interface CinemaScreeningContext {
  movieTitle?: string;
  resolvedMovieTitle?: string;
  movieId?: string;
  tmdbId?: string;
  sourceUrl?: string;
  sourceMovieId?: string;
  screeningStartsAtLocal?: string;
  venueName?: string;
}

export interface TheaterPerformanceContext {
  playTitle?: string;
  originalTitle?: string;
  performanceTitle?: string;
  startsAtLocal?: string;
  venueName?: string;
  sourceUrl?: string;
  targetPersonName?: string;
  candidateCount: number;
  searchAttemptCount: number;
  notes?: string;
}

/**
 * A `sports_match` follower's match, read off the item's own metadata (Tina writes the Highlightly
 * match there). The item `url` is a `highlightly://` identity that opens nothing, so `sourceUrl` is
 * Tina's `metadata.source_url` — a Google search that opens the match card — or, for items queued
 * before Tina wrote it, the same search built here from the teams, league and date.
 */
export interface SportsMatchContext {
  homeTeam?: string;
  awayTeam?: string;
  league?: string;
  round?: string;
  sport?: string;
  /** ISO with offset: Tina's `kickoff_local`, else the raw UTC `match.date`. */
  kickoff?: string;
  /** IANA zone the kickoff is shown in; absent ⇒ the browser's own zone. */
  timezone?: string;
  /** False ⇒ Highlightly's placeholder kickoff, shown as "time TBC". */
  timeConfirmed: boolean;
  sourceUrl?: string;
}

export interface ResolvedVenueCard {
  extractedName?: string;
  extractedAddress?: string;
  resolved: {
    id?: string;
    name?: string;
    address?: string;
    city?: string;
    cid?: string;
    thumbnailUrl?: string;
    confidence?: number;
  };
  statusLabel: string;
}

export interface ResolvedLineupCard {
  extractedName?: string;
  status: "linked" | "created" | "unmatched";
  resolved: {
    id?: string;
    name?: string;
    thumbnailUrl?: string;
    subline?: string;
  };
  reason?: string;
}

export function readMusicEventContext(
  item: PipelineItem,
  ctx: HitlContext,
): MusicEventContext | null {
  if (ctx.content_type !== "music_event") return null;

  const metadata = asRecord(item.metadata);
  const discover = asRecord(metadata.discover_payload);
  const extractedEvent = readFirstExtractedEvent(ctx);
  const venueResolution = asRecord(ctx.stage_outputs.venue_resolution);
  const venue = asRecord(venueResolution.venue);
  const geodata = asRecord(venue.geodata);
  const typeGenres = asRecord(ctx.stage_outputs.type_genres);
  const problem = asRecord(ctx.problem);

  // The row's OWN identity always lives in item.metadata (extracted_name /
  // extracted_start_iso, stamped per event at extract time). A coalesced
  // venue/movie HITL cluster promotes a new representative on force-close, and
  // promote_new_representative copies the *closed* representative's hitl_context
  // onto it — so stage_outputs.extract can describe a DIFFERENT event than this
  // row (curators saw every promoted item render as the first event, e.g. every
  // "The Stone" show showing as "Ochs, #1/4: TRIO"). Trust metadata for identity,
  // and only attribute the extract snapshot's event-specific fields
  // (about/dates/type/genres/lineup) when its name matches this row's own name.
  const ownName = firstText(metadata.extracted_name);
  const extractName = firstText(extractedEvent.name);
  const extractIsOwn =
    !ownName || !extractName || namesMatch(ownName, extractName);
  const ownEvent: Record<string, unknown> = extractIsOwn ? extractedEvent : {};

  const context: MusicEventContext = {
    name: firstText(
      metadata.extracted_name,
      extractedEvent.name,
      discover.title_artists,
      metadata.name,
    ),
    about: firstText(
      ownEvent.about,
      ownEvent.description,
      ownEvent.summary,
      metadata.about,
    ),
    startDate: firstText(
      metadata.extracted_start_iso,
      ownEvent.startDate,
      ownEvent.start_date,
    ),
    endDate: firstText(ownEvent.endDate, ownEvent.end_date),
    type: firstText(ownEvent.type, typeGenres.type),
    genreNames: readNames(ownEvent.genres ?? typeGenres.genres),
    // Venue is shared across the whole coalesced cluster, so it is safe to read
    // even from an inherited extract snapshot; fall back to the row's own
    // problem/discover payload when the extract event was suppressed above.
    venueName: firstText(
      extractedEvent.venueName,
      extractedEvent.venue_name,
      asRecord(extractedEvent.venue).name,
      venue.name,
      problem.extracted_venue_name,
      discover.venue_name,
    ),
    cityName: firstText(ownEvent.city, geodata.name, venue.area, metadata.city),
    lineupNames: readNames(ownEvent.lineup),
    timezone: firstText(ownEvent.timezone, venue.timezone, metadata.timezone),
    ticketingUrl: firstText(
      ownEvent.ticketingUrl,
      ownEvent.ticketUrl,
      ownEvent.ticket_url,
      ownEvent.url,
    ),
    sourceUrl: firstText(metadata.source_url, item.url),
    site: item.site,
    selectedImage: readSelectedEventImage(item, ctx, ownEvent),
  };

  return hasAnyValue(context) ? context : null;
}

// Normalized equality for extract-event attribution. Deliberately strict: when
// in doubt we suppress the extract snapshot's event-specific fields (showing
// blanks) rather than risk attributing a different event's lineup/about to this
// row — blank is recoverable, wrong is misleading.
function namesMatch(a: string, b: string): boolean {
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
  return norm(a) === norm(b);
}

export function readCinemaScreeningContext(
  item: PipelineItem,
  ctx: HitlContext,
): CinemaScreeningContext | null {
  if (ctx.content_type !== "cinema_screening") return null;

  const metadata = asRecord(item.metadata);
  const movieResolution = asRecord(ctx.stage_outputs.movie_resolution);
  const movieMaterialization = asRecord(
    ctx.stage_outputs.movie_materialization,
  );
  const screeningPayload = asRecord(movieResolution.screeningPayload);
  const source = asRecord(screeningPayload.source);
  const movieCandidate = asRecord(screeningPayload.movieCandidate);
  const selectedMovie = asRecord(movieResolution.selectedTmdbMovie);
  const materializedMovie = asRecord(movieMaterialization.movie);
  const venue = asRecord(screeningPayload.venue);
  const screening = asRecord(screeningPayload.screening);

  const context: CinemaScreeningContext = {
    movieTitle: firstText(
      metadata.movie_title,
      metadata.extracted_movie_title,
      movieCandidate.title,
      asRecord(movieResolution.movieEvidence).title,
    ),
    resolvedMovieTitle: firstText(
      metadata.resolved_movie_title,
      selectedMovie.title,
      materializedMovie.title,
    ),
    movieId: firstText(
      metadata.movie_id,
      metadata.resolved_movie_id,
      movieResolution.movieId,
      movieMaterialization.movieId,
      materializedMovie.id,
    ),
    tmdbId: firstText(
      metadata.tmdb_id,
      metadata.resolved_tmdb_id,
      movieResolution.tmdbId,
      selectedMovie.tmdbId,
      materializedMovie.tmdbId,
    ),
    sourceUrl: firstText(metadata.source_url, source.url, item.url),
    sourceMovieId: firstText(metadata.source_movie_id, source.sourceMovieId),
    screeningStartsAtLocal: firstText(
      metadata.screening_starts_at_local,
      screening.startsAtLocal,
      screening.rawText,
    ),
    venueName: firstText(
      metadata.venue_name,
      metadata.resolved_venue_name,
      venue.name,
    ),
  };

  return hasAnyValue(context) ? context : null;
}

export function readTheaterPerformanceContext(
  item: PipelineItem,
  ctx: HitlContext,
): TheaterPerformanceContext | null {
  if (ctx.content_type !== "theater_performance") return null;

  const problem = asRecord(ctx.problem);
  const playEvidence = asRecord(problem.play_evidence);
  const performancePayload = asRecord(problem.performance_payload);
  const playCandidate = asRecord(performancePayload.playCandidate);
  const performance = asRecord(performancePayload.performance);
  const venue = asRecord(performancePayload.venue);
  const target = asRecord(problem.target);
  const metadata = asRecord(item.metadata);

  const context: TheaterPerformanceContext = {
    playTitle: firstText(
      playEvidence.title,
      playCandidate.title,
      metadata.play_title,
    ),
    originalTitle: firstText(
      playEvidence.originalTitle,
      playEvidence.original_title,
      playEvidence.titleRaw,
    ),
    performanceTitle: firstText(performance.title, metadata.performance_title),
    startsAtLocal: firstText(
      performance.startsAtLocal,
      performance.starts_at_local,
      metadata.starts_at_local,
    ),
    venueName: firstText(venue.name, metadata.venue_name),
    sourceUrl: firstText(metadata.source_url, item.url),
    targetPersonName: firstText(target.name, target.originalName),
    candidateCount: asArray(problem.candidates).length,
    searchAttemptCount: asArray(problem.search_attempts).length,
    notes: firstText(problem.notes),
  };

  return hasAnyValue(context) ? context : null;
}

export function readSportsMatchContext(
  item: PipelineItem,
  ctx: HitlContext | null,
): SportsMatchContext | null {
  if (
    item.content_type !== "sports_match" &&
    ctx?.content_type !== "sports_match"
  )
    return null;
  const metadata = asRecord(item.metadata);
  const match = asRecord(metadata.match);
  const league = asRecord(metadata.league_payload);
  const homeTeam = firstText(asRecord(match.homeTeam).name);
  const awayTeam = firstText(asRecord(match.awayTeam).name);
  const leagueName = firstText(league.name);
  const kickoff = firstText(metadata.kickoff_local, match.date);
  const timezone = firstText(metadata.timezone);
  const sport = /^highlightly:\/\/([a-z]+)\//.exec(item.url ?? "")?.[1];
  const context: SportsMatchContext = {
    homeTeam,
    awayTeam,
    league: leagueName,
    round: firstText(match.round),
    sport,
    kickoff,
    timezone,
    timeConfirmed: metadata.time_confirmed !== false,
    sourceUrl:
      firstText(metadata.source_url) ??
      matchSearchUrl(homeTeam, awayTeam, leagueName, kickoff, timezone),
  };
  return homeTeam || awayTeam ? context : null;
}

/** Tina's `discover_plan.match_search_url`, for items queued before Tina wrote `source_url`. */
export function matchSearchUrl(
  homeTeam: string | undefined,
  awayTeam: string | undefined,
  league: string | undefined,
  kickoff: string | undefined,
  timezone: string | undefined,
): string | undefined {
  if (!homeTeam && !awayTeam) return undefined;
  const day = kickoff ? localDay(kickoff, timezone) : undefined;
  const query = [`${homeTeam ?? ""} vs ${awayTeam ?? ""}`, league, day]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(query).replace(/%20/g, "+")}`;
}

function localDay(
  iso: string,
  timezone: string | undefined,
): string | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone ?? "Europe/Athens",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function readMatchedVenue(
  item: PipelineItem,
  ctx: HitlContext,
): TextRef | null {
  const venueResolution = asRecord(ctx.stage_outputs.venue_resolution);
  const venue = asRecord(venueResolution.venue);
  const createdEntities = asRecord(item.created_entities);
  const metadata = asRecord(item.metadata);
  const musicContext = readMusicEventContext(item, ctx);
  const id = firstText(
    venueResolution.venue_id,
    venueResolution.venueId,
    venue.id,
    createdEntities.venue_id,
    metadata.resolved_venue_id,
  );
  const name = firstText(
    venue.name,
    metadata.resolved_venue_name,
    musicContext?.venueName,
  );

  return id || name ? { id, name } : null;
}

export function readMatchedArtists(
  item: PipelineItem,
  ctx: HitlContext,
): TextRef[] {
  const artistResolution = asRecord(ctx.stage_outputs.artist_resolution);
  const problem = asRecord(ctx.problem);
  const createdEntities = asRecord(item.created_entities);

  return dedupeRefs([
    ...readArtistRefsFromResolvedArtists(artistResolution.resolved_artists),
    ...readArtistRefsFromIds(artistResolution.artist_ids),
    ...readArtistRefsFromResolvedArtists(problem.resolved_artists),
    ...readArtistRefsFromIds(createdEntities.artist_ids),
  ]);
}

export function canCreateMoodEventFromHitl(
  item: PipelineItem,
  ctx: HitlContext | null | undefined,
): boolean {
  return item.status === "hitl" && ctx?.content_type === "music_event";
}

// Extracted -> resolved cards for the low_score review surface. Read from the
// completed stage outputs (deep-copied into hitl_context.stage_outputs), not the
// problem payload: at low_score the venue/artist stages already ran to "ok".
export function readResolvedVenueCard(
  item: PipelineItem,
  ctx: HitlContext,
): ResolvedVenueCard | null {
  const venueResolution = asRecord(ctx.stage_outputs.venue_resolution);
  const venue = asRecord(venueResolution.venue);
  const geodata = asRecord(venue.geodata);
  const metadata = asRecord(venue.metadata);
  const photos = asArray(asRecord(venue.media).photos);
  const problem = asRecord(ctx.problem);
  const musicContext = readMusicEventContext(item, ctx);
  // At low_score the problem is LowScoreProblem (no extracted_venue_* fields); the
  // extracted address lives on the extracted event payload.
  const extractedEvent = readFirstExtractedEvent(ctx);

  const id = firstText(
    venueResolution.venue_id,
    venueResolution.venueId,
    venue.id,
  );
  const name = firstText(venue.name, musicContext?.venueName);
  const extractedName = firstText(
    problem.extracted_venue_name,
    extractedEvent.venueName,
    musicContext?.venueName,
  );
  const extractedAddress = firstText(
    problem.extracted_venue_address,
    extractedEvent.venueAddress,
    asRecord(extractedEvent.venue).address,
  );

  if (!id && !name && !extractedName) return null;

  const matchType = firstText(
    venueResolution.match_type,
    venueResolution.matchType,
  );
  return {
    extractedName,
    extractedAddress,
    resolved: {
      id,
      name,
      address: firstText(venue.address, geodata.address),
      city: firstText(geodata.name, venue.area, musicContext?.cityName),
      cid: firstText(
        metadata.googleMapsCid,
        metadata.places_cid,
        venueResolution.places_cid,
      ),
      thumbnailUrl: firstText(asRecord(photos[0]).url),
      confidence:
        numberValue(venueResolution.confidence) ??
        numberValue(
          asRecord(venueResolution.resolver_stats).gemini_resolver_confidence,
        ),
    },
    statusLabel: venueStatusLabel(matchType, id),
  };
}

export function readResolvedLineupCards(
  _item: PipelineItem,
  ctx: HitlContext,
): ResolvedLineupCard[] {
  const artistResolution = asRecord(ctx.stage_outputs.artist_resolution);
  const cards: ResolvedLineupCard[] = [];

  for (const entry of asArray(artistResolution.resolved_artists)) {
    const record = asRecord(entry);
    const artist = asRecord(record.artist);
    const name = firstText(record.name, artist.name);
    if (!name) continue;
    const matchType = firstText(record.match_type, record.matchType);
    cards.push({
      extractedName: name,
      status: matchType === "created_with_spotify" ? "created" : "linked",
      resolved: {
        id: firstText(record.artist_id, record.artistId, artist.id),
        name: firstText(artist.name, name),
        thumbnailUrl: firstText(
          asRecord(asArray(asRecord(artist.media).photos)[0]).url,
        ),
        subline: artistSubline(artist),
      },
    });
  }

  const reasonByName = lineupAuditReasons(artistResolution);
  for (const raw of asArray(artistResolution.unmatched_lineup_names)) {
    const name = firstText(raw);
    if (!name) continue;
    cards.push({
      extractedName: name,
      status: "unmatched",
      resolved: {},
      reason: reasonByName.get(name),
    });
  }

  return cards;
}

function venueStatusLabel(matchType: string | undefined, id?: string): string {
  switch (matchType) {
    case "hitl_pick":
      return "Matched (curator)";
    case "hitl_created":
    case "auto_created":
      return "Created";
    default:
      return id ? "Matched" : "Unresolved";
  }
}

function artistSubline(artist: Record<string, unknown>): string | undefined {
  const spotify = asRecord(asRecord(artist.stats).spotify);
  const listeners = numberValue(spotify.monthlyListeners ?? spotify.followers);
  const genres = readNames(artist.genres);
  const parts: string[] = [];
  if (genres.length > 0) parts.push(genres.slice(0, 2).join(", "));
  if (listeners !== undefined)
    parts.push(`${listeners.toLocaleString()} listeners`);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function lineupAuditReasons(
  artistResolution: Record<string, unknown>,
): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const entry of asArray(artistResolution.per_artist)) {
    const record = asRecord(entry);
    const name = firstText(record.name);
    if (!name) continue;
    const verdict = asRecord(record.verdict);
    const reason = firstText(
      verdict.reason,
      verdict.note,
      record.reason,
      verdict.flag,
    );
    if (reason) reasons.set(name, reason);
  }
  return reasons;
}

function readFirstExtractedEvent(ctx: HitlContext): Record<string, unknown> {
  const extract = asRecord(ctx.stage_outputs.extract);
  return asRecord(asArray(extract.events)[0]);
}

function readSelectedEventImage(
  item: PipelineItem,
  ctx: HitlContext,
  extractedEvent: Record<string, unknown>,
): SelectedEventImage | undefined {
  const metadata = asRecord(item.metadata);
  const embedded = asRecord(
    extractedEvent.selectedImage ??
      extractedEvent.selected_image ??
      extractedEvent.selectedEventImage,
  );
  const metadataImage = asRecord(
    metadata.selected_event_image ??
      metadata.selectedImage ??
      metadata.selected_image ??
      metadata.selectedEventImage,
  );
  const selectedIndex = numberValue(
    embedded.index ??
      metadataImage.index ??
      metadata.selected_image_index ??
      extractedEvent.selectedImageIndex,
  );
  const classification = firstText(
    embedded.classification,
    metadataImage.classification,
    metadata.selected_image_classification,
    extractedEvent.imageClassification,
  );
  if (selectedIndex === undefined && !classification) return undefined;

  const fetchOutput = asRecord(ctx.stage_outputs.fetch);
  const candidates = asArray(
    fetchOutput.image_candidates ?? fetchOutput.imageCandidates,
  );
  const candidate = readImageCandidate(candidates, selectedIndex);
  const image: SelectedEventImage = {
    index: selectedIndex,
    classification,
    url: firstText(
      embedded.url,
      metadataImage.url,
      metadata.selected_image_url,
      candidate.url,
      candidate.image_url,
      candidate.imageUrl,
      candidate.src,
    ),
    ref: firstText(
      embedded.ref,
      metadataImage.ref,
      metadata.selected_image_ref,
      candidate.ref,
    ),
    source: firstText(
      embedded.source,
      metadataImage.source,
      metadata.selected_image_source,
      candidate.source,
    ),
  };

  return hasAnyValue(image) ? image : undefined;
}

function numberValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function readImageCandidate(
  candidates: unknown[],
  selectedIndex: number | undefined,
): Record<string, unknown> {
  if (selectedIndex === undefined) return {};
  const explicit = asRecord(
    candidates.find(
      (candidate) => numberValue(asRecord(candidate).index) === selectedIndex,
    ),
  );
  if (hasAnyValue(explicit)) return explicit;
  return asRecord(candidates[selectedIndex]);
}

function readNames(value: unknown): string[] {
  return asArray(value).flatMap((entry) => {
    if (typeof entry === "string") return entry.trim() ? [entry.trim()] : [];
    const record = asRecord(entry);
    const name = firstText(record.name, record.title);
    return name ? [name] : [];
  });
}

function readArtistRefsFromResolvedArtists(value: unknown): TextRef[] {
  return asArray(value).flatMap((entry) => {
    const record = asRecord(entry);
    const artist = asRecord(record.artist);
    const id = firstText(
      record.artist_id,
      record.artistId,
      artist.id,
      record.id,
    );
    const name = firstText(record.name, artist.name);
    return id || name ? [{ id, name }] : [];
  });
}

function readArtistRefsFromIds(value: unknown): TextRef[] {
  return asArray(value).flatMap((entry) => {
    const id = firstText(entry);
    return id ? [{ id }] : [];
  });
}

function dedupeRefs(refs: TextRef[]): TextRef[] {
  const seen = new Set<string>();
  const deduped: TextRef[] = [];

  for (const ref of refs) {
    const key = ref.id ?? ref.name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(ref);
  }

  return deduped;
}

function hasAnyValue(value: object): boolean {
  return Object.values(value as Record<string, unknown>).some((entry) => {
    if (entry === undefined || entry === null) return false;
    if (Array.isArray(entry)) return entry.length > 0;
    if (typeof entry === "string") return entry.length > 0;
    return true;
  });
}
